import {
  Avatar,
  Box,
  CircularProgress,
  Fade,
  IconButton,
  List,
  ListItemButton,
  Popover,
  Tooltip,
  Typography,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useProfileContext } from '../../profile.context';
import { useEffect, useMemo, useState } from 'react';
import { AvatarInput, downloadBlob, ImageAsset, UserImage, useAlert } from '@uplift/ui-v2';
import { useL10n } from '@uplift/hooks';
import { CloudUploadOutlined, PhotoCamera } from '@mui/icons-material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useUpdateAvatar } from '@uplift/queries';
import { useUploadAvatar } from '@uplift/utils';
import { getFormErrorMessage, getFormSuccessMessage, PROFILE_QUERY_KEY } from '../../profile.utils';

export const PROFILE_AVATAR_SIZE = 112;
const PROFILE_PHOTO_TITLE = 'Profile photo';

export const ProfileAvatar: React.VFC = () => {
  const l10n = useL10n();
  const alert = useAlert();
  const { classes } = useProfileAvatarStyles();
  const queryClient = useQueryClient();
  const uploadAvatar = useUploadAvatar();
  const { profile: { avatarAsset, avatarAssetOriginal, user } } = useProfileContext();
  const { updateAvatar } = useUpdateAvatar(user.id);
  const uploadMutation = useMutation({
    mutationFn: async ({ cropped, original }: ImageAsset) => {
      if (!cropped || !original) {
        alert.error(l10n.validations.photoUpload);
        return;
      }

      const imageCroppedFile = await downloadBlob(image.cropped);
      const assetCropped = await uploadAvatar(imageCroppedFile, image.name);
      const imageOriginalFile = await downloadBlob(image.original);
      const assetOriginal = (
        image.original === backendLink.original
          ? avatarAssetOriginal?.id
          : await uploadAvatar(imageOriginalFile, image.name)
      );

      const updateAvatarRes = await updateAvatar({
        avatarAssetId: assetCropped,
        avatarAssetIdToUpdate: avatarAsset?.id,
        originalAvatarAssetId: assetOriginal,
        originalAvatarAssetIdToUpdate: avatarAssetOriginal?.id
      });

      return updateAvatarRes;
    },
    onSuccess: async () => {
      alert.success(getFormSuccessMessage(PROFILE_PHOTO_TITLE));
      await queryClient.invalidateQueries(PROFILE_QUERY_KEY(user.id));
    },
    onError: async () => {
      alert.error(getFormErrorMessage(PROFILE_PHOTO_TITLE));
    }
  });
  const [ anchorEl, setAnchorEl ] = useState<HTMLDivElement | null>(null);
  const [ menuOpen, setMenuOpen ] = useState(false);
  const originalUrl = avatarAssetOriginal?.url;
  const croppedUrl = avatarAsset?.url;

  const backendLink = useMemo<ImageAsset>(() => ({
    name: 'avatar.jpg',
    original: originalUrl ?? undefined,
    cropped: croppedUrl ?? undefined,
  }), [ originalUrl, croppedUrl ]);
  const [ image, setImage ] = useState<ImageAsset>(backendLink);

  useEffect(() => {
    if (image.cropped !== backendLink.cropped) {
      uploadMutation.mutate(image);
    }
  }, [ image ]);

  const imageJsx = useMemo(() => {
    if (avatarAsset) {
      return (
        <UserImage
          imageUrl={avatarAsset?.url}
          maxWidth="100%"
        />
      );
    }

    // placeholder
    return <Avatar className={classes.avatar} />;
  }, [ avatarAsset, classes.avatar ]);

  const editIcon = (
    <IconButton className={classes.cameraIconButton}>
      <PhotoCamera />
    </IconButton>
  );

  return (
    <div className={classes.imageContainer} ref={ref => setAnchorEl(ref)}>
      {imageJsx}
      {!menuOpen && !uploadMutation.isLoading && (
        <Tooltip
          title="Edit Photo"
          classes={{ tooltip: classes.tooltip }}
        >
          <div
            className={classes.editClickDiv}
            onClick={() => setMenuOpen(true)}
          >
            {editIcon}
          </div>
        </Tooltip>
      )}
      {menuOpen && editIcon}
      {uploadMutation.isLoading && (
        <Box
          position="absolute"
          display="flex"
          alignItems="center"
          justifyContent="center"
          width="100%"
          height="100%"
        >
          <CircularProgress color="secondary" />
        </Box>
      )}
      <AvatarInput
        initialImage={image}
        onImageChange={newImage => setImage(newImage)}
        setError={(error) => error && alert.error(error)}
        overrideActionsMenu={({ initialImage, htmlFor, onEditCurrentPhoto }) => {
          if (!initialImage) {
            return (
              <label htmlFor={htmlFor} className={classes.label}>
                <CloudUploadOutlined color="primary" fontSize="small" />
                <Typography color="primary">{l10n.matching.profilePhoto.choosePhoto}</Typography>
              </label>
            );
          }

          const editCurrentPhoto = () => {
            setMenuOpen(false);
            onEditCurrentPhoto(initialImage);
          };

          return (
            <Popover
              open={menuOpen}
              anchorEl={anchorEl}
              onClose={() => setMenuOpen(false)}
              keepMounted
              className={classes.popper}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'center',
              }}
              slots={{
                transition: Fade
              }}
            >
              <List>
                {avatarAsset && <ListItemButton className={classes.listItem} onClick={editCurrentPhoto}>Edit current photo</ListItemButton>}
                <ListItemButton className={classes.listItem} component="label" htmlFor={htmlFor} onClick={() => setMenuOpen(false)}>Choose new photo</ListItemButton>
              </List>
            </Popover>
          );
        }}
      />
    </div>
  );
};

const useProfileAvatarStyles = makeStyles({ name: 'ProfileAvatar' })(theme => ({
  avatar: {
    width: '100%',
    height: '100%',
    borderRadius: 0,
  },
  imageContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: PROFILE_AVATAR_SIZE,
    height: PROFILE_AVATAR_SIZE,
    minWidth: PROFILE_AVATAR_SIZE,
    position: 'relative',
  },
  popper: {
    marginTop: theme.spacing(0.5),
    zIndex: 10000,
  },
  listItem: {
    '&:hover': {
      background: theme.palette.secondary.background,
    }
  },
  cameraIconButton: {
    background: theme.palette.secondary.main,
    '&:hover': {
      background: theme.palette.secondary.main,
    },
    color: theme.palette.common.white,
    position: 'absolute',
    width: '24px',
    height: '24px',
    bottom: '-12px',
    right: '-12px',
    '& svg': {
      fontSize: '18px',
    }
  },
  label: {
    width: '100%',
    height: '100%',
    background: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    gap: theme.spacing(1),
    textAlign: 'center',
    position: 'absolute',
    cursor: 'pointer'
  },
  editClickDiv: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    cursor: 'pointer',
  },
  tooltip: {
    marginTop: theme.spacing(0.5),
    fontSize: theme.typography.body2.fontSize,
  },
}));
